import React, { useContext, useEffect, useRef, useCallback } from 'react';
import { useSelector } from '@xstate/react';
import { useTranslation } from 'react-i18next';

import {
  Alert,
  BodyText,
  Box,
  Button,
  Card,
  Modal,
} from '@chargify/custom-ui-components';
import { useToastMessages } from '@chargify/toasts';
import { formatCurrency } from '@chargify/formatters';
import { useReactivateSubscription } from '../../../../hooks';
import { subscriptionsSelectors } from '../../../../machines';
import { GlobalStateContext } from '../../../../contexts';
import { reactivateSubscriptionSelectors } from '../../../../machines/reactivateSubscription';

type ReactivationProps = {
  /**
   * Modal display mode
   */
  displayMode: 'layer' | 'window';
  /**
   * Close event handler
   */
  onClose: () => void;
  /**
   * Catalog id
   */
  catalogId: number;
  /**
   * Subscription id
   */
  subscriptionId: number;
};

const Content = ({
  onClose,
  displayMode,
  catalogId,
  subscriptionId,
}: ReactivationProps) => {
  const { t } = useTranslation(['subscriptions-manager']);
  const modalRef = useRef(null);
  const toastService = useToastMessages();

  const reactivateService = useReactivateSubscription(
    catalogId,
    subscriptionId
  );

  const { subscriptionsService } = useContext(GlobalStateContext);
  const { subscription } = useSelector(
    subscriptionsService,
    useCallback(
      subscriptionsSelectors.getSubscriptionById(catalogId, subscriptionId),
      []
    )
  );
  const {
    reactivateSubscriptionSuccess,
    reactivateSubscriptionError,
    reactivateSubscriptionLoading,
  } = useSelector(
    reactivateService,
    reactivateSubscriptionSelectors.getReactivateSubscriptionStatus
  );
  const reactivationError = useSelector(
    reactivateService,
    reactivateSubscriptionSelectors.getErrorCode
  );

  useEffect(() => {
    if (reactivateSubscriptionSuccess) {
      onClose();

      const toastMessage = t('reactivate_subscription.reactivate_success');
      toastService.show({
        level: 'success',
        message: toastMessage,
      });
    }
  }, [reactivateSubscriptionSuccess, onClose, toastService, t]);

  const onReactivation = () => {
    reactivateService.send({
      type: 'REACTIVATE_SUBSCRIPTION',
    });
  };

  const getErrorMsgTitle = () =>
    reactivationError?.errorCode === 'NotEligible'
      ? t('reactivate_subscription.not_eligible_title')
      : t('reactivate_subscription.unexpected_problem');

  const getErrorMsgDescription = () =>
    reactivationError?.errorCode === 'NotEligible'
      ? t('reactivate_subscription.not_eligible_description')
      : t('reactivate_subscription.unable_to_reactivate');

  return (
    <>
      <Card
        border={displayMode === 'layer' ? 'sharp' : 'rounded'}
        ref={modalRef}
      >
        <Card.Header>
          <Modal.Title
            onClose={onClose}
            title={t('reactivate_subscription.reactivate_title')}
          />
        </Card.Header>
        <Card.Body>
          {!reactivateSubscriptionError && (
            <>
              <Box marginBottom={6}>
                <BodyText>
                  {t('reactivate_subscription.reactivate_description', {
                    planName: subscription.product.name,
                  })}
                </BodyText>
              </Box>
              <Alert
                title={t('reactivate_subscription.you_will_be_charged', {
                  planName: subscription.product.name,
                  existingBalance: formatCurrency(
                    subscription.currency,
                    subscription.balanceInCents
                  ),
                  baseFee: formatCurrency(
                    subscription.currency,
                    subscription.product.priceInCents
                  ),
                })}
                color="primary"
              >
                <Alert.TextMessage>
                  {t('reactivate_subscription.make_sure_your_payment_info')}
                </Alert.TextMessage>
              </Alert>
            </>
          )}
          {reactivateSubscriptionError && (
            <Alert color="danger" title={getErrorMsgTitle()}>
              <Alert.TextMessage>{getErrorMsgDescription()}</Alert.TextMessage>
            </Alert>
          )}
        </Card.Body>
        <Card.Footer>
          <Button.Group alignment="right">
            <Button
              color="secondary"
              label={t('cancel', {
                ns: 'common',
              })}
              onClick={onClose}
              disabled={reactivateSubscriptionLoading}
            />
            <Button
              color="primary"
              label={t('reactivate_subscription.confirm_reactivation')}
              disabled={reactivateSubscriptionError}
              isLoading={reactivateSubscriptionLoading}
              onClick={onReactivation}
            />
          </Button.Group>
        </Card.Footer>
      </Card>
    </>
  );
};

export default Content;
